import TableColumn from "table/TableColumn";
import { ColumnType } from "./ColumnType";

export interface ClauseDispatcher {
    acceptRoot(v: RootClause): void;
    acceptSearch(v: SearchClause): void;
    acceptAnd(v: AndClause): void;
    acceptOr(v: OrClause): void;
    acceptOperator(v: OperatorClause): void;
    acceptColumnChoice(v: ColumnClause): void;
    acceptLiteral(v: LiteralClause): void;
    // more to be added.
}

export interface FilterClause {
    // Add a new clause with an AND
    and(tc: ColumnPath, o: OrdOperator, v:any) : void;

    // Probably not used
    map<U>(callbackfn: (value: FilterClause) => U, thisArg?: any): U[];

    // Invoke the relevant method on acceptor from each node in the tree.
    visit(acceptor: ClauseDispatcher): void;

    // Find the given clause in the tree and remove it.
    removeClause(c: FilterClause): void;

    // Replace my child clause with the given clause.
    replaceClause(replaceMe: FilterClause, withClause: FilterClause): void;

    // Whether it's worthwhile filtering by my sub-clause. 
    isViable(): boolean;
}

abstract class BinaryClause implements FilterClause {
    parent: FilterClause;
    left: FilterClause;
    right: FilterClause;

    constructor(parent: FilterClause, l: FilterClause, r: FilterClause) {
        this.parent = parent;
        this.left = l;
        this.right = r;
    }

    abstract visit(acceptor: ClauseDispatcher): void;

    and(tc: ColumnPath, o: OrdOperator, v:any) {
        let comparec = new OperatorClause(
            new ColumnClause(tc),
            o,
            new LiteralClause(v, tc[tc.length-1].type)
        );
        this.right = new AndClause(this, this.right, comparec);
    }

    map<U>(callbackfn: (value: FilterClause) => U, thisArg?: any): U[] {
        return this.left.map(callbackfn).concat(this.right.map(callbackfn));
    }

    removeClause(c: FilterClause): void {
        if (this.left === c) {
            this.parent.replaceClause(this, this.right);
        } else if (this.right === c) {
            this.parent.replaceClause(this, this.left);
        } else {
            this.left.removeClause(c);
            this.right.removeClause(c);
        }
    }

    replaceClause(replaceMe: FilterClause, withClause: FilterClause): void {
        throw new Error("Method not implemented.");
    }

    isViable = () => {
        return this.left.isViable() && this.right.isViable();
    }
}

export class RootClause implements FilterClause {
    clause: FilterClause;
    className = "RootClause" // for debugging

    constructor() {
        this.clause = new SearchClause();
    }

    and(tc: ColumnPath, o: OrdOperator, v:any) {
        let isEmpty : boolean = false;
        this.clause.visit(new class implements ClauseDispatcher {
            acceptRoot(v: RootClause): void {
            }
            acceptSearch(v: SearchClause): void {
                isEmpty = true;
            }
            acceptAnd(v: AndClause): void {
            }
            acceptOr(v: OrClause): void {
            }
            acceptOperator(v: OperatorClause): void {
            }
            acceptColumnChoice(v: ColumnClause): void {
            }
            acceptLiteral(v: LiteralClause): void {
            }
        }());

        if (isEmpty) {
            let comparec = new OperatorClause(
                  new ColumnClause(tc),
                o,
                new LiteralClause(v, tc[tc.length-1].type)
            );
            this.clause = new AndClause(this, this.clause, comparec);
        } else {
            this.clause.and(tc, o, v);
        }
    }

    map<U>(callbackfn: (value: FilterClause) => U, thisArg?: any): U[] {
        if (undefined === this.clause) {
            return []
        } else {
            return this.clause.map(callbackfn);
        }
    }

    visit(acceptor: ClauseDispatcher) {
        acceptor.acceptRoot(this);
    }

    removeClause = (c: FilterClause) => {
        if (this.clause === c) {
            this.clause = new SearchClause();
        } else {
            this.clause.removeClause(c);
        }
    }

    replaceClause = (replaceMe: FilterClause, withClause: FilterClause) => {
        if (this.clause === replaceMe) {
            this.clause = withClause;
        }
    }

    isViable = () => {
        return this.clause.isViable();
    }
}

// For generic multi-column text search.
export class SearchClause implements FilterClause {
    public static readonly ID_SEARCHCLAUSE = "ID_SEARCH"

    value: string = "";
    className = "SearchClause" // for debugging

    and(tc: ColumnPath, o: OrdOperator, v:any) {}

    map<U>(callbackfn: (value: FilterClause) => U, thisArg?: any): U[] {
        return []
    }

    visit = (acceptor: ClauseDispatcher) => {
        acceptor.acceptSearch(this);
    }

    removeClause(c: FilterClause): void {
    }

    replaceClause(replaceMe: FilterClause, withClause: FilterClause): void {
    }

    // Used for working out which selected value in a <select> is.
    get id(): string {
        return "Search";
    }

    isViable = () => {
        return this.value.length > 0;
    }
}

export class AndClause extends BinaryClause {
    className = "AndClause" // for debugging

    visit(acceptor: ClauseDispatcher) {
        acceptor.acceptAnd(this);
    }
}

export class OrClause extends BinaryClause {
    className = "OrClause" // for debugging

    visit(acceptor: ClauseDispatcher) {
        acceptor.acceptOr(this);
    }
}

export class NotClause implements FilterClause {
    parent: FilterClause;
    clause: FilterClause;

    constructor(parent: FilterClause, child: FilterClause) {
        this.parent = parent;
        this.clause = child;
    }

    // TODO: be smarter with types.
    and(tc: ColumnPath, o: OrdOperator, v:any) {}

    map<U>(callbackfn: (v: FilterClause) => U, thisArg?: any): U[] {
        if (undefined === this.clause) {
            return []
        } else {
            return this.clause.map(callbackfn);
        }
    }

    visit(acceptor: ClauseDispatcher) {
        throw new Error("Not implemented yet.")
    }

    removeClause(c: FilterClause): void {
        this.parent.replaceClause(this, c);
    }


    replaceClause(replaceMe: FilterClause, withClause: FilterClause): void {
        if (this.clause === replaceMe) {
            this.clause = withClause;
        }
    }

    isViable = () => {
        return this.clause.isViable();
    }
}

/*
Usually a filter would be something like 
    [column] [operator] [value]
 e.g. 
    [user/name] [=] ["bob"]
 "
 ]
*/
export class OperatorClause implements FilterClause {
    operator: OrdOperator; // TODO  | TextOperator;
    left: ValueClause;
    right: ValueClause;
    className = "OperatorClause" // for debugging

    constructor(left: ValueClause, operator: OrdOperator, right: ValueClause) {
        this.operator = operator;
        this.left = left;
        this.right = right;
    }

    // TODO: be smarter with types.
    and(tc: ColumnPath, o: OrdOperator, v:any) {}

    map<U>(callbackfn: (value: FilterClause) => U, thisArg?: any): U[] {
        return [callbackfn(this)];
    }

    visit(acceptor: ClauseDispatcher) {
        acceptor.acceptOperator(this);
    }

    removeClause(c: FilterClause): void {
    }

    replaceClause(replaceMe: FilterClause, withClause: FilterClause): void {
        throw new Error("Invalid invocation.");
    }

    // Used for rendering <select>, so we know which is selected.
    get id(): string {
        return this.left.id;
    }

    get value(): string {
        if (this.right instanceof LiteralClause) {
            return String(this.right.value);
        } else {
            throw new Error("no value")
        }
    }

    isViable = () => {
        return this.left.isViable() && this.right.isViable();
    }
}

/* A value can be either a literal value (a string or a number), or a selected column. */

export interface ValueClause {
    visit(acceptor: ClauseDispatcher): void;
    get id(): string;
    isViable(): boolean;
}

export class LiteralClause implements ValueClause {
    private _value: any;
    private _type: ColumnType;

    constructor(value: any, t: ColumnType) {
        this._value = value;
        this._type = t;
    }

    visit(acceptor: ClauseDispatcher) {
        acceptor.acceptLiteral(this);
    }

    get id() {
        // TODO
        return String(this._value);
    }

    isViable = () => {
        return this._value && this._value.toString().length > 0;
    }

    get type() {
        return this._type;
    }

    get value(): any {
        return this._value;
    }

    set value(v: any) {
        this._value = v;
    }
}

export type ColumnPath = Array<TableColumn>; 

export class ColumnClause implements ValueClause {
    column?: Array<TableColumn>;

    /* In a filter, the left hand side of an operator can be an 
       arbitrary expression, e.g. WHERE LOWER(x) like '...'.

       This will all need to be refactored later if we want to 
       implement this in a GUI. For now, the left-hand-side
       can just be a column from the table (rather than
       from the query)
       */
    constructor(columnPath: ColumnPath) {
        this.column = columnPath;
    }

    public visit(acceptor: ClauseDispatcher) {
        acceptor.acceptColumnChoice(this);
    }

    get id() {
        return this.asPath();
    }

    public asPath = () => {
        if (!this.column) {
            return "";
        }

        let result : string = "";
        let first : boolean = true;
        for(let each of this.column) {
            if (!first) {
                result = result + "/";
            }
            result = result + each.name ?? "";
            first = false;
        }
        return result;
    }

    public get value() {
        if (this.column && this.column.length > 0) {
            return String(this.column[this.column.length-1]);
        } else {
            return "";
        }
    }

    isViable = () => true;
}

export enum OrdOperator {
    equals,
    greaterThan,
    lessThan,
    //in, // Must be list
}

export enum TextOperator {
    equals,
    greaterThan,
    lessThan,
    like, // Must be string.
    startsWith,
    endsWith,
}

